import { useState, useCallback } from 'react';
import i18next from 'i18next';

import { SrpFilterApiParams } from 'lib/srpFilterTransformers';
import { useRouter } from 'next/router';
import SearchAPI, { SearchResult } from 'lib/apis/SearchApi';
import useOptimizelyDecisions from './useOptimizelyDecisions';

export type UseSearchVehiclesResult = [SearchVehicles, UseSearchVehiclesState];

export type SearchVehicles = (params: SrpFilterApiParams) => Promise<void>;

export interface UseSearchVehiclesState {
  isPending: boolean;
  error?: unknown;
  data: SearchResult;
  hasFetchedOnce: boolean;
}

export default function useSearchVehicles(): UseSearchVehiclesResult {
  const [isPending, setIsPending] = useState(true);
  const [error, setError] = useState<unknown>();
  const [data, setData] = useState<SearchResult>({
    vehicles: [],
    totalCount: 0,
    adInfo: null,
    facetCounts: {},
  });
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false);
  const router = useRouter();
  const { getDecision } = useOptimizelyDecisions();
  const sortOrder = getDecision('sortOrder');

  const searchVehicles = useCallback(
    async (params: SrpFilterApiParams) => {
      try {
        setIsPending(true);
        setError(null);

        const searchParams = {
          locale: i18next.language,
          ...(sortOrder && { sort_filter: 'combined_test_score:desc' }),
          ...params,
        };

        const d = await SearchAPI.searchVehicles(searchParams);
        setData(d);
        setIsPending(false);
      } catch (err) {
        if (err.message === 'Valid coordinates were not provided') {
          router.push(router.pathname);
        } else {
          setIsPending(false);
          setError(err);
        }
      } finally {
        setHasFetchedOnce(true);
      }
    },
    [router, sortOrder]
  );

  return [searchVehicles, { isPending, error, data, hasFetchedOnce }];
}
